import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import { createPinia } from 'pinia'

// Side-effect import: pulls in the CSS reset, Poppins @font-face, and the
// `--background*`, `--black`, `--white` custom properties that every layout
// and component depends on. Both build targets need it — on web Blade picks
// it up via `@vite('resources/js/main.ts')` (Vite emits the CSS link as a
// dependency of the JS entry), on Capacitor the static index.html does the
// same through the same JS bundle. Don't move it back into Blade alone or
// the Capacitor build loses every shared style.
import '@scss/app.css'

import App from './App.vue'
import router from './router'
import {
  initErrorReporting,
  installErrorReportingStatePlugin,
} from './lib/telemetry/errorReporting'
import de from './locales/de.json'
import en from './locales/en.json'
import { installKeyboardInset } from './lib/keyboard-inset'
import { installAudioUnlock } from './lib/audio'

const i18n = createI18n({
  fallbackLocale: 'de',
  legacy: false,
  locale: 'de',
  messages: { de, en },
})

const app = createApp(App)

const pinia = createPinia()

// Before `app.use(pinia)`: the plugin registers a global Sentry event processor
// and has to be in place before any store is instantiated. Its allowlist is
// deny-by-default — see lib/telemetry/sentryStoreAllowlist.ts.
installErrorReportingStatePlugin(pinia)

app.use(pinia)

// Before the router and before mount, so an error thrown while resolving the
// first route is still captured. This application had no error reporting at all
// until 2026-09; see docs/error-reporting.md for what that cost.
initErrorReporting(app)

app.use(router)
app.use(i18n)

// Publishes --keyboard-inset-bottom on <html> whenever the on-screen keyboard
// shows/hides. Screens with fixed-position controls near the bottom (e.g.
// ExercisesCreate's speichern button) compose it into their `bottom` calc so
// the control rides above the keyboard on iOS and Android.
installKeyboardInset()

// Arms the Web Audio context on the first tap. Only does anything on WebKit
// browsers, where the training screen plays its cues through Web Audio so they
// don't silence Safari's speech recognizer — see lib/audio.ts. Here rather than
// in the training screen because its chunk is lazy-loaded and the unlocking
// gesture usually happens on a screen before it.
installAudioUnlock()

// Patient bootstrap (POST /patient/bootstrap) is intentionally deferred to
// LaunchTerms.vue → auth.bootstrapPatient(); minting eagerly here would
// pre-populate the token and skip the onboarding gate in router/index.ts.
app.mount('#app')
